import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import { AdvancedMarker } from '@vis.gl/react-google-maps'
import { get } from 'lodash'
import PropTypes from 'prop-types'
import React from 'react'

import { COMMON, WARNING } from 'theme/colors'
import { resolveMarkerColors, parseStopFeature } from 'utils/markerColorUtils'

const getMinWidth = totalStopCount => {
  if (totalStopCount >= 10000) return '3.5rem'
  if (totalStopCount >= 1000) return '3.25rem'
  return '2.5rem'
}

const MarkerPill = ({ label, bgColor, textColor, borderColor, minWidth = '2.5rem', onClick, onMouseEnter, onMouseLeave, haloColor }) => {
  const chipBoxShadow = `inset 0 0 0 1.5px ${COMMON.white}`
  // A selected stop recolours its own pill border (not an added ring), so the
  // pill footprint is identical to unselected — matches the WebGL pill.
  const pillBorderColor = haloColor ?? borderColor
  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex',
        justifyContent: 'center',
        alignItems: 'center',
        transform: 'translateY(-50%)',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.15s ease-out',
        '&:hover': onClick ? { transform: 'translateY(-50%) scale(1.1)' } : {},
      }}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Chip
        label={label}
        size="small"
        sx={{
          backgroundColor: bgColor,
          color: textColor,
          border: `3px solid ${pillBorderColor}`,
          boxShadow: chipBoxShadow,
          fontWeight: 900,
          height: '24px',
          borderRadius: '12px',
          minWidth,
          '& .MuiChip-label': { paddingLeft: '6px', paddingRight: '6px' },
          '&:hover': { backgroundColor: bgColor },
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '-8px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: '7px solid transparent',
          borderRight: '7px solid transparent',
          borderTop: `7px solid ${pillBorderColor}`,
          zIndex: -1,
        }}
      />
    </Box>
  )
}

const StopMarker = ({ feature, totalStopCount = 0, onClick, onMouseEnter, onMouseLeave, haloColor = null }) => {
  const [isHovered, setIsHovered] = React.useState(false)
  const parsed = parseStopFeature(feature)
  if (!parsed) return null

  const { lng, lat, icon, label, id: stopId, properties } = parsed
  const { bgColor, textColor, borderColor } = resolveMarkerColors(icon, properties)

  const handleClick = onClick ? () => onClick(feature) : undefined
  const handleMouseEnter = onMouseEnter
    ? () => {
        setIsHovered(true)
        onMouseEnter({ lat, lng }, stopId)
      }
    : () => setIsHovered(true)
  const handleMouseLeave = () => {
    setIsHovered(false)
    if (onMouseLeave) onMouseLeave()
  }

  // Stacking mirrors BulkDeckMarkerLayer's stackRank: unassigned on top, staged next, rest baseline.
  const isUnassignedPin = icon === 'to-be-assigned-marker' || icon === 'unassigned-marker'
  const isStagedForMove = get(properties, '_isStagedForMove') === true
  let baseZ
  if (isUnassignedPin) baseZ = 5000
  else if (isStagedForMove) baseZ = 4000
  return (
    <AdvancedMarker position={{ lat, lng }} zIndex={isHovered ? 9998 : baseZ}>
      <MarkerPill
        bgColor={bgColor}
        borderColor={borderColor}
        haloColor={haloColor}
        label={label}
        minWidth={getMinWidth(totalStopCount)}
        textColor={textColor}
        /* eslint-disable react/jsx-no-bind -- handlers are body-local arrows that close over the parsed feature; useCallback would require feature in the deps and re-create the same closure */
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        /* eslint-enable react/jsx-no-bind */
      />
    </AdvancedMarker>
  )
}

const ToBeAssignedMarker = ({ latitude, longitude }) => {
  if (!latitude || !longitude) return null
  return (
    <AdvancedMarker position={{ lat: parseFloat(latitude), lng: parseFloat(longitude) }}>
      <Box
        height={18}
        sx={{
          backgroundColor: COMMON.white,
          border: `2px solid ${WARNING.main}`,
          borderRadius: '50%',
        }}
        width={18}
      />
    </AdvancedMarker>
  )
}

MarkerPill.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  bgColor: PropTypes.string.isRequired,
  textColor: PropTypes.string.isRequired,
  borderColor: PropTypes.string.isRequired,
  minWidth: PropTypes.string,
  onClick: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  haloColor: PropTypes.string,
}

ToBeAssignedMarker.propTypes = {
  latitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  longitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

StopMarker.propTypes = {
  feature: PropTypes.shape({
    geometry: PropTypes.shape({ coordinates: PropTypes.arrayOf(PropTypes.number) }),
    properties: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      icon: PropTypes.string,
      color: PropTypes.string,
      'border-color': PropTypes.string,
      position: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }).isRequired,
  totalStopCount: PropTypes.number,
  onClick: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  haloColor: PropTypes.string,
}

export { ToBeAssignedMarker }
export default StopMarker
